"use client";

// Primitive kit — the entire dashboard surface should be built from these,
// not bespoke className soup per page. Sharp corners, hairline rules, no
// chrome. Editorial display + monospace data.
//
// This module is a client module: AccountBadge reads the aggregate-codes
// context (a hook), so the primitives ship in the client bundle. All real
// consumers are already client pages; server components may still render
// these (a server component can render a client component).

import React from "react";
import { useConstituentCodes } from "@/components/aggregate-codes";

export function Page({
  title,
  eyebrow,
  children,
  actions,
}: {
  title: React.ReactNode;
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-10">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-rule pb-6">
        <div>
          {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
          <h1 className="display text-4xl sm:text-5xl text-foreground">{title}</h1>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
      </header>
      {children}
    </div>
  );
}

export function Section({
  title,
  aside,
  subtitle,
  children,
}: {
  title?: React.ReactNode;
  aside?: React.ReactNode;
  /** One-line prose below the header row. Use to explain what the
   *  section measures when two sections on the same page slice the
   *  same numbers by different dimensions (e.g. NIFTY cycle P&L
   *  vs P&L by expiry month on /accounts/[id]). */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      {(title || aside) && (
        <div className="flex items-baseline justify-between border-b-2 border-rule pb-3">
          {title && (
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              {title}
            </h2>
          )}
          {aside && <div className="eyebrow text-mute">{aside}</div>}
        </div>
      )}
      {subtitle && (
        <p className="-mt-2 text-sm text-mute">{subtitle}</p>
      )}
      {children}
    </section>
  );
}

export function Tile({
  label,
  value,
  hint,
  variant = "default",
  size = "default",
  className = "",
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  variant?: "default" | "up" | "down" | "accent";
  size?: "default" | "hero";
  className?: string;
}) {
  const valColor =
    variant === "up"
      ? "text-up"
      : variant === "down"
        ? "text-down"
        : variant === "accent"
          ? "text-accent"
          : "text-foreground";
  return (
    <div className={`flex flex-col gap-2 border-l border-rule pl-4 ${className}`}>
      <div className="eyebrow">{label}</div>
      <div
        className={`num ${valColor} ${
          size === "hero" ? "display text-5xl sm:text-6xl" : "text-2xl"
        }`}
      >
        {value}
      </div>
      {hint && <div className="text-xs text-mute">{hint}</div>}
    </div>
  );
}

export function Crumbs({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav className="eyebrow flex items-center gap-2 text-mute">
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden>—</span>}
          {it.href ? (
            <a href={it.href} className="transition hover:text-foreground">
              {it.label}
            </a>
          ) : (
            <span className="text-foreground">{it.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

/* Table primitives — borderless, hairline-bottom rule per row, mono data.
 *
 * Sticky first column. Pass `sticky` on <Table>, then `sticky` on the
 * first <Th>/<Td> of every row. Wide tables (trades, cycle drilldowns,
 * per-broker rollup, portfolio summary) become horizontally scrollable
 * with the leftmost identifying column (Underlying / Cycle / Time /
 * Broker etc.) frozen against the viewport. Same pattern that shipped
 * on /reports/portfolio-summary; the props make it declarative
 * everywhere else.
 *
 * Implementation notes:
 *  - Sticky mode flips the <table> to `border-separate border-spacing-0`
 *    because Chrome's default `border-collapse: collapse` bleeds
 *    hairline borders through the sticky cell's opaque background.
 *  - Sticky cells use `bg-background` (not `bg-inherit`) so they cover
 *    scrolled-under content cleanly. Row-hover highlight is
 *    intentionally NOT applied to the frozen column — the whole point
 *    of the freeze is to be a stable identifier while the operator
 *    scans other columns.
 */

const STICKY_TABLE_CLS = "border-separate border-spacing-0";
const STICKY_TH_CLS = "sticky left-0 bg-background z-10";
const STICKY_TD_CLS = "sticky left-0 bg-background";

export function Table({
  children,
  className = "",
  sticky = false,
}: {
  children: React.ReactNode;
  className?: string;
  sticky?: boolean;
}) {
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table
        className={`min-w-full ${sticky ? STICKY_TABLE_CLS : ""}`}
      >
        {children}
      </table>
    </div>
  );
}

export function Thead({ children }: { children: React.ReactNode }) {
  return (
    <thead className="border-b border-rule">
      <tr>{children}</tr>
    </thead>
  );
}

const ALIGN_CLS: Record<"left" | "right" | "center", string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

export function Th({
  children,
  align = "left",
  className = "",
  sticky = false,
}: {
  children?: React.ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
  sticky?: boolean;
}) {
  return (
    <th
      className={`eyebrow py-1.5 pr-4 ${ALIGN_CLS[align]} font-normal text-mute ${sticky ? STICKY_TH_CLS : ""} ${className}`}
    >
      {children}
    </th>
  );
}

export function Tbody({ children }: { children: React.ReactNode }) {
  return <tbody>{children}</tbody>;
}

export function Tr({
  children,
  className = "",
  onClick,
  asLinkHref,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  asLinkHref?: string;
}) {
  const baseCls =
    "border-b border-rule/50 transition hover:bg-white/[0.02]";
  if (asLinkHref) {
    return (
      <tr
        className={`${baseCls} cursor-pointer ${className}`}
        onClick={() => (window.location.href = asLinkHref)}
      >
        {children}
      </tr>
    );
  }
  return (
    <tr className={`${baseCls} ${onClick ? "cursor-pointer" : ""} ${className}`} onClick={onClick}>
      {children}
    </tr>
  );
}

export function Td({
  children,
  align = "left",
  mono = true,
  className = "",
  sticky = false,
}: {
  children?: React.ReactNode;
  align?: "left" | "right" | "center";
  mono?: boolean;
  className?: string;
  sticky?: boolean;
}) {
  return (
    <td
      className={`py-1.5 pr-4 text-sm ${ALIGN_CLS[align]} ${mono ? "num" : ""} ${sticky ? STICKY_TD_CLS : ""} ${className}`}
    >
      {children}
    </td>
  );
}

/* Pill — small status / kind tag */

export function Pill({
  children,
  variant = "default",
  size = "sm",
}: {
  children: React.ReactNode;
  variant?: "default" | "up" | "down" | "accent" | "outline";
  size?: "sm" | "lg";
}) {
  const cls =
    variant === "up"
      ? "bg-up/15 text-up"
      : variant === "down"
        ? "bg-down/15 text-down"
        : variant === "accent"
          ? "bg-accent/15 text-accent"
          : variant === "outline"
            ? "border border-rule text-mute"
            : "bg-white/5 text-foreground";
  const sizeCls =
    size === "lg"
      ? "px-2.5 py-1 text-[14px] tracking-wider"
      : "px-1.5 py-0.5 text-[10px] tracking-widest";
  return (
    <span className={`inline-flex items-center whitespace-nowrap uppercase ${sizeCls} ${cls}`}>
      {children}
    </span>
  );
}

/* AccountBadge — client-code pill for real client accounts, "PRO"
   for house / test / synthetic. Renders nothing when a client account
   has no clientcode set (would just be dead visual noise). Use next
   to the broker pill wherever an account row is displayed. */

export function AccountBadge({
  is_client_account,
  clientcode,
  size = "sm",
  accountId,
}: {
  is_client_account: boolean;
  clientcode: string | null | undefined;
  size?: "sm" | "lg";
  // When set, an aggregate account (e.g. MASLTD6) shows a tooltip of the
  // desk codes live this cycle (issue #178). Non-aggregate accounts render
  // unchanged. Safe to omit — no tooltip then.
  accountId?: number;
}) {
  const codes = useConstituentCodes(accountId);
  const pill = !is_client_account ? (
    <Pill variant="accent" size={size}>PRO</Pill>
  ) : clientcode ? (
    <Pill variant="outline" size={size}>{clientcode}</Pill>
  ) : null;
  if (pill === null) return null;
  if (codes.length === 0) return pill;

  const label = `Live this cycle: ${codes.join(", ")}`;
  // Dotted underline hints the badge is hoverable; the bubble shows on hover
  // and keyboard focus, with `title`/`aria-label` as the accessible fallback.
  return (
    <span
      className="group relative inline-flex cursor-help underline decoration-dotted decoration-mute/50 underline-offset-4 outline-none"
      tabIndex={0}
      title={label}
      aria-label={label}
    >
      {pill}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 hidden -translate-x-1/2 whitespace-nowrap border border-rule bg-background px-2 py-1 num text-[10px] normal-case tracking-normal text-foreground shadow-lg group-hover:block group-focus-within:block"
      >
        {codes.join(" · ")}
      </span>
    </span>
  );
}

/* Button — sharp, hairline border, no fills except primary */

export function Button({
  children,
  variant = "default",
  size = "default",
  className = "",
  ...rest
}: {
  children: React.ReactNode;
  variant?: "default" | "primary" | "ghost";
  /** `sm` is the toolbar/pagination-strip sizing — tighter padding,
   *  keeps the eyebrow uppercase styling. Default is page-header sizing. */
  size?: "default" | "sm";
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const variantCls =
    variant === "primary"
      ? "bg-foreground text-background hover:opacity-90"
      : variant === "ghost"
        ? "text-mute hover:text-foreground"
        : "border border-rule text-foreground hover:border-foreground";
  const sizeCls = size === "sm" ? "px-2 py-1" : "px-3 py-2";
  return (
    <button
      {...rest}
      className={`eyebrow transition disabled:opacity-40 ${sizeCls} ${variantCls} ${className}`}
    >
      {children}
    </button>
  );
}

/* MenuItem — full-width clickable row inside a dropdown menu.
 * Body-text sized (not eyebrow), left-aligned, hairline hover. */
export function MenuItem({
  children,
  className = "",
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={`block w-full text-left px-3 py-1 text-sm hover:bg-white/[0.04] disabled:opacity-40 ${className}`}
    >
      {children}
    </button>
  );
}

/* ToggleChip — pill-shaped button with on/off state. Used for
 * categorical-filter chips on DataTable. Sharp corners + hairline
 * border like Pill, but interactive. */
export function ToggleChip({
  children,
  selected = false,
  ...rest
}: {
  children: React.ReactNode;
  selected?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const cls = selected
    ? "border-foreground text-foreground"
    : "border-rule text-mute hover:border-foreground/50";
  return (
    <button {...rest} className={`text-xs border px-2 py-0.5 transition ${cls}`}>
      {children}
    </button>
  );
}

export function Input(
  props: React.InputHTMLAttributes<HTMLInputElement>,
) {
  return (
    <input
      {...props}
      className={`border-b border-rule bg-transparent px-0 py-1.5 text-sm text-foreground outline-none focus:border-foreground ${
        props.className ?? ""
      }`}
    />
  );
}

export function Select(
  props: React.SelectHTMLAttributes<HTMLSelectElement>,
) {
  return (
    <select
      {...props}
      className={`border-b border-rule bg-transparent px-0 py-1.5 text-sm text-foreground outline-none focus:border-foreground ${
        props.className ?? ""
      }`}
    />
  );
}

export function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="eyebrow">{label}</span>
      {children}
    </label>
  );
}
